import React from 'react';
import { Alert, Avatar, Box, Button, CircularProgress, Stack, TextField, Typography, InputAdornment, MenuItem } from '@mui/material';
import {
  Business,
  CalendarMonth,
  CameraAlt,
  ContactPhone,
  Email,
  LockPerson,
  Person,
  Phone,
  Save,
  Star,
  TableRestaurant,
  Work,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import TopAppBar from '../layout/TopAppBar';
import InterestsSelection from '../utilities/InterestsOptions';
import { ActionButton, PageContainer, SectionHeader, SurfaceCard } from '../ui/ConvivialComponents';
import {
  normalizeProfilePhoneNumber,
  profilePhoneRegex,
  profileRoleOptions,
  profileSectorOptions,
} from '../../constants/profileOptions';

function formatMonthYear(value) {
  const date = value ? new Date(value) : null;
  if (!date || Number.isNaN(date.getTime())) return '-';
  return date.toLocaleDateString('it-IT', { month: '2-digit', year: 'numeric' });
}

export default function ProfileEditForm({
  completionRequired = false,
  isLoading,
  imagePreview,
  userAvatarUrl,
  userCompleteName,
  userFirstName,
  setUserFirstName,
  userLastName,
  setUserLastName,
  userDescription,
  setUserDescription,
  userProfession,
  setUserProfession,
  userSector,
  setUserSector,
  userInterests,
  setUserInterests,
  userEmail,
  setUserEmail,
  userPhoneNumber,
  setUserPhoneNumber,
  userPreferenceZones,
  setUserPreferenceZones,
  userCreatedAt,
  tableOrganisatorCount,
  tableParticipationCount,
  fieldErrors,
  handleImageClick,
  handleImageChange,
  handleUpdateProfile,
}) {
  const navigate = useNavigate();
  const normalizedPhoneNumber = normalizeProfilePhoneNumber(userPhoneNumber);
  const isPhoneNumberInvalid = normalizedPhoneNumber !== '' && !profilePhoneRegex.test(normalizedPhoneNumber);

  return (
    <>
      <TopAppBar title="ConvivialApp.it" showAvatar={false} />
      <PageContainer maxWidth="900px" sx={{ pt: 'calc(var(--cv-top-bar-height) + 32px)' }}>
        {isLoading ? (
          <CircularProgress />
        ) : (
          <Stack spacing={3}>
            {completionRequired && (
              <Alert severity="info">Completa il profilo con nome, cognome e numero di telefono per continuare su Convivial.</Alert>
            )}
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <Box sx={{ position: 'relative', display: 'inline-block' }}>
                <Avatar
                  src={imagePreview || userAvatarUrl}
                  alt={userCompleteName}
                  sx={{
                    width: 132,
                    height: 132,
                    border: '4px solid #fff',
                    boxShadow: '0 10px 30px rgba(29,29,27,0.12)',
                    outline: '4px solid #ffd9dd',
                    cursor: 'pointer',
                  }}
                  onClick={handleImageClick}
                />
                <Button
                  aria-label="Cambia foto profilo"
                  onClick={handleImageClick}
                  sx={{
                    position: 'absolute',
                    bottom: 0,
                    right: 0,
                    minWidth: 0,
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    bgcolor: 'primary.main',
                    color: '#fff',
                    border: '2px solid #fff',
                    '&:hover': { bgcolor: 'primary.dark' },
                  }}
                >
                  <CameraAlt fontSize="small" />
                </Button>
              </Box>
              <input type="file" id="imageUpload" style={{ display: 'none' }} accept="image/*" onChange={handleImageChange} />
            </Box>

            <SurfaceCard>
              <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ sm: 'center' }} justifyContent="space-between" spacing={1.5} sx={{ mb: 2 }}>
                <SectionHeader icon={<Person />} title="Informazioni pubbliche" sx={{ mb: 0 }} />
                {/* <Chip selected>Visibili ai commensali</Chip> */}
              </Stack>
              <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
                Queste informazioni saranno visibili alle persone con cui condividerai un tavolo.
              </Alert>
              <Stack spacing={2}>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                  <TextField
                    label="Nome"
                    required
                    value={userFirstName || ''}
                    onChange={(e) => setUserFirstName(e.target.value)}
                    error={fieldErrors.userFirstName}
                    helperText={fieldErrors.userFirstName ? 'Campo obbligatorio' : ''}
                    fullWidth
                  />
                  <TextField
                    label="Cognome"
                    required
                    value={userLastName || ''}
                    onChange={(e) => setUserLastName(e.target.value)}
                    error={fieldErrors.userLastName}
                    helperText={fieldErrors.userLastName ? 'Campo obbligatorio' : ''}
                    fullWidth
                  />
                </Box>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                  <TextField
                    label="Ruolo"
                    select
                    value={userProfession || 'Non definita'}
                    onChange={(e) => setUserProfession(e.target.value)}
                    fullWidth
                    slotProps={{
                      input: {
                        startAdornment: (
                          <InputAdornment position="start">
                            <Work sx={{ color: 'secondary.main', mr: 1 }} />
                          </InputAdornment>
                        ),
                      },
                    }}
                  >
                    {profileRoleOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    label="Settore"
                    select
                    value={userSector || 'Non definita'}
                    onChange={(e) => setUserSector(e.target.value)}
                    fullWidth
                    slotProps={{
                      input: {
                        startAdornment: (
                          <InputAdornment position="start">
                            <Business sx={{ color: 'secondary.main', mr: 1 }} />
                          </InputAdornment>
                        ),
                      },
                    }}
                  >
                    {profileSectorOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Box>
                <TextField
                  label="Bio"
                  value={userDescription || ''}
                  onChange={(e) => setUserDescription(e.target.value)}
                  multiline
                  minRows={4}
                  fullWidth
                />
                <TextField
                  label="Zone preferite"
                  value={userPreferenceZones || ''}
                  onChange={(e) => setUserPreferenceZones(e.target.value)}
                  placeholder="Milano Centro, Brera, Porta Romana"
                  fullWidth
                  helperText="Separale con una virgola."
                />
                <Box>
                  <SectionHeader icon={<Star />} title="Interessi" sx={{ mb: 1 }} />
                  <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1.5 }}>
                    Convivial prova sempre ad aiutarti suggerendo il prossimo tavolo da raggiungere. Se compili interessi e zone, potremo avvisarti quando nasce un tavolo vicino alle tue preferenze.
                  </Typography>
                  <InterestsSelection viewState="edit" userInterests={userInterests} setUserInterests={setUserInterests} />
                </Box>
              </Stack>
            </SurfaceCard>

            <SurfaceCard sx={{ borderStyle: 'dashed', bgcolor: 'rgba(229,226,222,0.2)' }}>
              <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ sm: 'center' }} justifyContent="space-between" spacing={1.5} sx={{ mb: 2 }}>
                <SectionHeader icon={<LockPerson />} title="Contatti personali" sx={{ mb: 0 }} />
                {/* <Chip>Solo durante il tavolo</Chip> */}
              </Stack>
              <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
                Il tuo numero di telefono resta privato. Per contattare gli altri commensali usa la chat interna del tavolo.
              </Alert>
              <Stack spacing={2}>
                <TextField
                  label="Numero di telefono"
                  required
                  type="tel"
                  value={userPhoneNumber || ''}
                  onChange={(e) => setUserPhoneNumber(e.target.value)}
                  error={Boolean(fieldErrors.userPhoneNumber) || isPhoneNumberInvalid}
                  helperText={
                    fieldErrors.userPhoneNumber || isPhoneNumberInvalid
                      ? 'Numero non valido: inserisci +, prefisso internazionale e numero (da 7 a 15 cifre).'
                      : 'Inserisci il prefisso internazionale, ad esempio +39, +44 o +1.'
                  }
                  fullWidth
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <Phone sx={{ color: 'secondary.main', mr: 1 }} />
                        </InputAdornment>
                      ),
                    },
                  }}
                />
                <TextField
                  label="Email"
                  value={userEmail || ''}
                  onChange={(e) => setUserEmail(e.target.value)}
                  fullWidth
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <Email sx={{ color: 'secondary.main', mr: 1 }} />
                        </InputAdornment>
                      ),
                    },
                  }}
                />
              </Stack>
            </SurfaceCard>

            <SurfaceCard>
              <SectionHeader icon={<TableRestaurant />} title="Statistiche" />
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 2 }}>
                <StatTile icon={<CalendarMonth />} label="Membro da" value={formatMonthYear(userCreatedAt)} />
                <StatTile icon={<TableRestaurant />} label="Tavoli creati" value={tableOrganisatorCount || 0} />
                <StatTile icon={<ContactPhone />} label="Tavoli frequentati" value={tableParticipationCount || 0} />
              </Box>
              <Button
                variant="outlined"
                fullWidth
                onClick={() => navigate('/user/tables')}
                sx={{ mt: 2, borderRadius: 2.5, minHeight: 44, color: 'primary.main', borderColor: 'primary.main', fontWeight: 600 }}
              >
                I miei tavoli
              </Button>
            </SurfaceCard>

            <Box sx={{ position: { xs: 'sticky', md: 'static' }, bottom: 'calc(var(--cv-bottom-nav-height) + 12px)', zIndex: 3 }}>
              <ActionButton
                variant="container"
                onClick={handleUpdateProfile}
                disabled={isLoading}
                icon={<Save />}
                sx={{ maxWidth: 520, mx: 'auto' }}
              >
                Aggiorna Profilo
              </ActionButton>
            </Box>
          </Stack>
        )}
      </PageContainer>
    </>
  );
}

function StatTile({ icon, label, value }) {
  return (
    <Box sx={{ p: 2, bgcolor: 'background.surfaceLow', borderRadius: 2, border: '1px solid', borderColor: 'outline.variant' }}>
      <Box sx={{ color: 'secondary.main', display: 'flex', mb: 1 }}>{icon}</Box>
      <Typography variant="caption" sx={{ color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
        {label}
      </Typography>
      <Typography variant="h3" sx={{ color: 'text.primary', mt: 0.5 }}>
        {value}
      </Typography>
    </Box>
  );
}
